import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Download, FileText, Shield, Settings, ChevronRight, ShoppingCart, Building, ChevronLeft, Lock, ExternalLink, ShoppingBag } from 'lucide-react';
import { products } from '../data/products';
import { ProductPlaceholder } from '../components/ProductPlaceholder';
import { OrderRequestModal } from '../components/OrderRequestModal';
import { FormSelect } from '../components/FormSelect';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { getShopProductUrl } from '../data/shopUrls';
export function ProductPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = products.find(p => p.slug === slug);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    org: '',
    sector: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  if (!product) {
    return <Navigate to="/products" replace data-id="element-1071" />;
  }
  // Find previous and next products within the same category
  const categoryProducts = products.filter(p => p.category === product.category);
  const currentIndex = categoryProducts.findIndex(p => p.slug === product.slug);
  const prevProduct = currentIndex > 0 ? categoryProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < categoryProducts.length - 1 ? categoryProducts[currentIndex + 1] : null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };
  return <div className="bg-white pb-24" data-id="element-1072">
      <OrderRequestModal isOpen={isOrderModalOpen} onClose={() => setIsOrderModalOpen(false)} productName={product.name} data-id="element-1073" />

      {/* Breadcrumbs */}
      <Breadcrumbs items={[{
      label: 'Products',
      to: '/products'
    }, {
      label: product.categoryName,
      to: `/products#${product.category}`
    }, {
      label: product.name
    }]} data-id="element-1074" />

      {/* Previous / Next Product Navigation */}
      <div className="bg-white border-b border-steel-200" data-id="element-1075">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex items-center justify-between" data-id="element-1076">
          {prevProduct ? <Link to={`/products/${prevProduct.slug}`} className="flex items-center gap-2 text-steel-500 hover:text-cg-orange-600 transition-colors group" data-id="element-1077">
              <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" data-id="element-1078" />
              <div className="text-left" data-id="element-1079">
                <span className="text-[10px] font-bold uppercase tracking-wider text-steel-400 block" data-id="element-1080">
                  Previous
                </span>
                <span className="text-xs font-bold text-navy-900 group-hover:text-cg-orange-600 transition-colors line-clamp-1" data-id="element-1081">
                  {prevProduct.name}
                </span>
              </div>
            </Link> : <div data-id="element-1082" />}

          <Link to={`/products#${product.category}`} className="hidden sm:flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-steel-400 hover:text-cg-orange-600 transition-colors border border-steel-200 px-3 py-1.5 rounded-sm" data-id="element-1083">
            View All {product.categoryName}
          </Link>

          {nextProduct ? <Link to={`/products/${nextProduct.slug}`} className="flex items-center gap-2 text-steel-500 hover:text-cg-orange-600 transition-colors group" data-id="element-1084">
              <div className="text-right" data-id="element-1085">
                <span className="text-[10px] font-bold uppercase tracking-wider text-steel-400 block" data-id="element-1086">
                  Next
                </span>
                <span className="text-xs font-bold text-navy-900 group-hover:text-cg-orange-600 transition-colors line-clamp-1" data-id="element-1087">
                  {nextProduct.name}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-1088" />
            </Link> : <div data-id="element-1089" />}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-12" data-id="element-1090">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20" data-id="element-1091">
          {/* Image Gallery */}
          <div data-id="element-1092">
            <div className="aspect-[4/3] bg-slate-100 mb-4 overflow-hidden rounded-sm border border-steel-200 relative" data-id="element-1093">
              <ProductPlaceholder aspectRatio="4/3" className="w-full h-full" data-id="element-1094" />
              <div className="absolute top-4 left-4 flex flex-col gap-2" data-id="element-1095">
                {product.sectors.map(s => <span key={s} className="bg-navy-900/90 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-sm w-fit shadow-sm" data-id="element-1096">
                    {s}
                  </span>)}
              </div>
              <div className="absolute top-4 right-4" data-id="element-1097">
                <span className="bg-cg-orange-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-sm shadow-sm flex items-center gap-1" data-id="element-1098">
                  Available in Inventory
                </span>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4" data-id="element-1099">
              {[0, 1, 2, 3].map(i => <button key={i} onClick={() => setActiveImage(i)} className={`aspect-square bg-slate-50 border rounded-sm overflow-hidden transition-all ${activeImage === i ? 'border-cg-orange-600 ring-1 ring-cg-orange-600' : 'border-steel-200 hover:border-steel-400'}`} data-id="element-1100">
                  <ProductPlaceholder aspectRatio="1/1" className="w-full h-full" data-id="element-1101" />
                </button>)}
            </div>
          </div>

          {/* Product Info */}
          <div data-id="element-1102">
            <div className="flex items-center gap-2 mb-4" data-id="element-1103">
              <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider" data-id="element-1104">
                Product Preview
              </span>
            </div>
            <h1 className="text-4xl font-bold text-navy-900 mb-6" data-id="element-1105">
              {product.name}
            </h1>
            <p className="text-xl text-steel-600 mb-8 leading-relaxed font-light" data-id="element-1106">
              {product.technicalDescription}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10" data-id="element-1107">
              <div className="bg-slate-50 p-4 border-l-4 border-cg-orange-600" data-id="element-1108">
                <h3 className="text-xs font-bold text-navy-900 uppercase tracking-wider mb-1" data-id="element-1109">
                  Lead Time
                </h3>
                <p className="text-sm text-steel-700" data-id="element-1110">{product.leadTime}</p>
              </div>
              <div className="bg-slate-50 p-4 border-l-4 border-navy-900" data-id="element-1111">
                <h3 className="text-xs font-bold text-navy-900 uppercase tracking-wider mb-1" data-id="element-1112">
                  Configuration
                </h3>
                <p className="text-sm text-steel-700" data-id="element-1113">
                  {product.configuration}
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-10" data-id="element-1114">
              <div className="flex items-center gap-3" data-id="element-1115">
                <CheckCircle2 className="w-5 h-5 text-cg-orange-600" data-id="element-1116" />
                <span className="text-sm font-medium text-navy-900" data-id="element-1117">
                  Manufactured to strict technical specifications
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-1118">
                <CheckCircle2 className="w-5 h-5 text-cg-orange-600" data-id="element-1119" />
                <span className="text-sm font-medium text-navy-900" data-id="element-1120">
                  Available via public sector frameworks
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-1121">
                <CheckCircle2 className="w-5 h-5 text-cg-orange-600" data-id="element-1122" />
                <span className="text-sm font-medium text-navy-900" data-id="element-1123">
                  Full technical documentation available
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-1124">
              <a href={getShopProductUrl(product.slug)} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm transition-colors gap-2" data-id="element-1125">
                <ShoppingBag className="w-4 h-4" data-id="element-1126" /> Continue to Inventory
              </a>
              <a href="#request-documentation" className="flex-1 inline-flex items-center justify-center px-8 py-4 border border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white font-bold uppercase tracking-wider rounded-sm transition-colors gap-2" data-id="element-1127">
                <FileText className="w-4 h-4" data-id="element-1128" /> Request Documentation
              </a>
            </div>

            {/* Organisation Procurement Panel - NEW */}
            <div className="bg-slate-50 border border-steel-200 p-4 rounded-sm flex items-start gap-3" data-id="element-1129">
              <Building className="w-5 h-5 text-steel-400 mt-1 flex-shrink-0" data-id="element-1130" />
              <div data-id="element-1131">
                <h4 className="text-sm font-bold text-navy-900 mb-1" data-id="element-1132">
                  Organisation Procurement
                </h4>
                <p className="text-xs text-steel-600" data-id="element-1133">
                  For bulk orders, framework procurement, or bespoke
                  requirements, please contact our team directly or request
                  documentation below.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Configuration & Options - Dossier Style */}
        <div className="mb-20 bg-slate-50 border border-steel-200 p-8 md:p-12 rounded-sm" data-id="element-1134">
          <div className="flex items-center gap-3 mb-8" data-id="element-1135">
            <Settings className="w-6 h-6 text-cg-orange-600" data-id="element-1136" />
            <h2 className="text-2xl font-bold text-navy-900" data-id="element-1137">
              Options and Configuration
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1138">
            <div data-id="element-1139">
              <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-1140">
                Variants
              </h3>
              <ul className="space-y-2" data-id="element-1141">
                {product.variants.map((v, i) => <li key={i} className="text-sm text-steel-600 flex items-start gap-2" data-id="element-1142">
                    <span className="w-1.5 h-1.5 bg-cg-orange-600 rounded-full mt-1.5 flex-shrink-0" data-id="element-1143" />
                    {v}
                  </li>)}
              </ul>
            </div>
            <div className="md:col-span-2" data-id="element-1144">
              <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-1145">
                Technical Specifications
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1146">
                {product.specifications.map((spec, i) => <div key={i} className="flex justify-between border-b border-steel-200/50 pb-2" data-id="element-1147">
                    <span className="text-sm font-bold text-navy-900" data-id="element-1148">
                      {spec.field}
                    </span>
                    <span className="text-sm text-steel-600" data-id="element-1149">{spec.value}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Deployed In / Applications - Dossier Panels */}
        <div className="mb-20" data-id="element-1150">
          <div className="flex items-center gap-3 mb-8" data-id="element-1151">
            <Shield className="w-6 h-6 text-cg-orange-600" data-id="element-1152" />
            <h2 className="text-2xl font-bold text-navy-900" data-id="element-1153">Deployed In</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1154">
            {product.applications.map((app, i) => <Link key={i} to={`/sectors/${app.sector.toLowerCase()}`} className="flex items-start gap-6 p-8 bg-white border border-steel-200 border-l-4 border-l-transparent hover:border-l-cg-orange-600 transition-all group rounded-sm" data-id="element-1155">
                <div data-id="element-1156">
                  <h4 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1157">
                    {app.sector} Sector
                  </h4>
                  <p className="text-steel-600 mb-4" data-id="element-1158">{app.use}</p>
                  <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider flex items-center gap-1" data-id="element-1159">
                    View Sector <ArrowRight className="w-3 h-3" data-id="element-1160" />
                  </span>
                </div>
              </Link>)}
          </div>
        </div>

        {/* Documentation Request - Dossier Style */}
        <div id="request-documentation" className="bg-navy-900 p-8 md:p-12 rounded-sm text-white" data-id="element-1161">
          <div className="max-w-3xl mx-auto" data-id="element-1162">
            <div className="text-center mb-10" data-id="element-1163">
              <FileText className="w-12 h-12 text-cg-orange-600 mx-auto mb-4" data-id="element-1164" />
              <h2 className="text-3xl font-bold mb-4" data-id="element-1165">
                Request Product Documentation
              </h2>
              <p className="text-steel-400 text-lg" data-id="element-1166">
                Access detailed technical data sheets, compliance certificates,
                and user guides for the {product.name}.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-10" data-id="element-1167">
              {['Technical Datasheet', 'Sizing Guide', 'Compliance Certificate', 'Care Instructions'].map((doc, i) => <div key={i} className="bg-transparent border border-white/20 px-4 py-2 rounded-sm text-center" data-id="element-1168">
                  <span className="text-xs font-bold text-steel-300 uppercase tracking-wider" data-id="element-1169">
                    {doc}
                  </span>
                </div>)}
            </div>

            {!submitted ? <form onSubmit={handleSubmit} className="space-y-4 bg-navy-800 p-8 rounded-sm border border-white/5" data-id="element-1170">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1171">
                  <input type="text" id="firstName" required placeholder="First Name *" className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600" value={formData.firstName} onChange={handleChange} data-id="element-1172" />
                  <input type="text" id="lastName" required placeholder="Last Name *" className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600" value={formData.lastName} onChange={handleChange} data-id="element-1173" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1174">
                  <input type="email" id="email" required placeholder="Work Email *" className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600" value={formData.email} onChange={handleChange} data-id="element-1175" />
                  <input type="text" id="org" required placeholder="Organisation *" className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600" value={formData.org} onChange={handleChange} data-id="element-1176" />
                </div>

                <FormSelect id="sector" required variant="dark" value={formData.sector} onChange={handleChange} data-id="element-1177">
                  <option value="" className="text-steel-500" data-id="element-1178">
                    Select Sector *
                  </option>
                  <option value="Defence" data-id="element-1179">Defence</option>
                  <option value="Police" data-id="element-1180">Police</option>
                  <option value="Prisons" data-id="element-1181">Prisons</option>
                  <option value="Medical" data-id="element-1182">Medical</option>
                  <option value="Other" data-id="element-1183">Other</option>
                </FormSelect>

                <button type="submit" className="w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-colors flex items-center justify-center gap-2" data-id="element-1184">
                  <Download className="w-4 h-4" data-id="element-1185" /> Request Documentation Pack
                </button>
                <div className="flex items-start gap-2" data-id="element-1186">
                  <Lock className="w-3.5 h-3.5 text-cg-orange-600 mt-0.5 flex-shrink-0" data-id="element-1187" />
                  <p className="text-xs text-steel-500 leading-relaxed" data-id="element-1188">
                    This form is encrypted and all submissions are treated in
                    strict confidence. Your data is processed in accordance with
                    our Privacy Policy.
                  </p>
                </div>
              </form> : <div className="bg-navy-800 border border-white/10 p-12 rounded-sm text-center" data-id="element-1189">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" data-id="element-1190" />
                <h3 className="text-white font-bold text-2xl mb-4" data-id="element-1191">
                  Request Received
                </h3>
                <p className="text-steel-400 text-lg" data-id="element-1192">
                  Thank you. The documentation pack for {product.name} has been
                  sent to your email address.
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}